/**
 * flightRecalculationRules — ScheduleDraft 航班字段派生规则层。
 *
 * 本模块将表单与 Timetable 中的 Route→BH→STA、STD→STA、STA→BH、
 * Airport→Route 匹配语义收敛为 UI 无关的纯函数接口。它位于编辑命令管道
 * 之前，负责给提交层提供可回填的 patches、可展示 warnings 与 validations 占位。
 *
 * 设计文档 → docs/30_editing_publish/30_local_edit_and_undo.md
 * 骨架关联 → components/ScheduleDraftFlightForm.jsx (表单联动), components/editor/timetable/useTimetableGridEditing.js (单元格提交), lib/editCommandPipeline.js (ChangeSet 构建), lib/flightDataUtils.js (航路 BH 读取)
 */

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { getFlightRouteBlockTime, getRouteBlockTime, getRouteMinBlockTime } from "./flightDataUtils";
import {
    findMatchingRoute,
    getRouteById,
} from "./routeUtils";
import { getFlightBlockTimeMinutes, getIsoDay, normalizeMinutes } from "./flightTimingUtils";

// index.js/App.jsx do not initialize dayjs plugins yet; keep module-level UTC registration.
dayjs.extend(utc);

const MAX_DERIVATION_DEPTH = 2;

/**
 * 根据 STD 和 block time 计算 STA。
 * @param {string|null} std - ISO 8601 格式的 STD
 * @param {number|null} blockTimeMinutes
 * @returns {string|null} ISO 8601 格式的 STA，或 null
 */
export function calculateSta(std, blockTimeMinutes) {
    const normalizedBlockTime = normalizeMinutes(blockTimeMinutes);
    if (!std || normalizedBlockTime == null) {
        return null;
    }

    const stdTime = dayjs.utc(std);
    if (!stdTime.isValid()) {
        return null;
    }

    const sta = stdTime.add(normalizedBlockTime, "minute");
    return sta.isValid() ? sta.toISOString() : null;
}

export function buildRouteSnapshot(route, blockTimeMinutes, minBlockTimeMinutes) {
    if (!route) {
        return null;
    }

    return {
        id: route.id,
        route_name: route.route_name,
        block_time_minutes: blockTimeMinutes,
        min_block_time_minutes: minBlockTimeMinutes,
        departure_airport: route.departure_airport,
        arrival_airport: route.arrival_airport,
    };
}

function calculateBlockTimeMinutes(std, sta) {
    if (!std || !sta) {
        return null;
    }

    const stdTime = dayjs.utc(std);
    const staTime = dayjs.utc(sta);
    if (!stdTime.isValid() || !staTime.isValid()) {
        return null;
    }

    const effectiveStaTime = staTime.isBefore(stdTime) ? staTime.add(1, "day") : staTime;
    return effectiveStaTime.diff(stdTime, "minute");
}

function getCurrentBlockTime(currentFlight) {
    return getFlightBlockTimeMinutes(currentFlight);
}

function getCurrentRouteId(currentFlight) {
    return currentFlight?.flight_route_id ?? currentFlight?.route_snapshot?.id ?? null;
}

function buildCurrentRouteSnapshotFallback(currentFlight) {
    const routeId = getCurrentRouteId(currentFlight);
    if (routeId === undefined || routeId === null || routeId === "") {
        return null;
    }

    const snapshot = currentFlight?.route_snapshot || {};
    return {
        ...snapshot,
        id: snapshot.id ?? routeId,
        route_name: snapshot.route_name ?? currentFlight?.flight_route_name ?? currentFlight?.route_name ?? null,
        departure_airport: snapshot.departure_airport ?? currentFlight?.departure_airport ?? null,
        arrival_airport: snapshot.arrival_airport ?? currentFlight?.arrival_airport ?? null,
        block_time_minutes: snapshot.block_time_minutes ?? getFlightRouteBlockTime(currentFlight),
        min_block_time_minutes: snapshot.min_block_time_minutes,
    };
}

function getCurrentRoute(currentFlight, referenceData) {
    const routeId = getCurrentRouteId(currentFlight);
    return getRouteById(referenceData, routeId) || buildCurrentRouteSnapshotFallback(currentFlight);
}

function getRouteDisplayLabel(route, currentFlight) {
    return route?.route_name
        || currentFlight?.flight_route_name
        || currentFlight?.route_name
        || route?.id
        || getCurrentRouteId(currentFlight)
        || "当前航路";
}

function getRouteTiming(route) {
    return {
        blockTimeMinutes: getRouteBlockTime(route),
        minBlockTimeMinutes: getRouteMinBlockTime(route),
    };
}

function buildRouteTimingWarnings(route, currentFlight) {
    if (!route) {
        return [];
    }

    const { blockTimeMinutes, minBlockTimeMinutes } = getRouteTiming(route);
    const routeLabel = getRouteDisplayLabel(route, currentFlight);
    const warnings = [];
    if (blockTimeMinutes === null) {
        warnings.push(`航路 ${routeLabel} 缺少计划轮档时间，无法按航路 BH 自动计算 STA`);
    }
    if (minBlockTimeMinutes === null) {
        warnings.push(`航路 ${routeLabel} 缺少最小轮档时间配置`);
    }
    return warnings;
}

function getDateString(value) {
    if (!value) {
        return null;
    }

    const parsed = dayjs.utc(value);
    return parsed.isValid() ? parsed.format("YYYY-MM-DD") : null;
}

function deriveStdDatePatches(newValue, currentFlight, previousValue) {
    const newFlightDate = getDateString(newValue);
    if (!newFlightDate) {
        return {};
    }

    const previousFlightDate = getDateString(previousValue) ?? getDateString(currentFlight?.flight_date);
    if (!previousFlightDate || previousFlightDate === newFlightDate) {
        return {};
    }

    return {
        flight_date: newFlightDate,
        day_of_week: getIsoDay(newValue),
    };
}

// ─── 规则常量 ───────────────────────────────────────────────────────────────

/** 航路变更 → 根据航路 Block Time 重算 STA */
export const ROUTE_CHANGED_RECALC_STA = "ROUTE_CHANGED_RECALC_STA";

/** STD 变更 → 根据当前 Block Time 重算 STA */
export const STD_CHANGED_RECALC_STA = "STD_CHANGED_RECALC_STA";

/** STA 变更 → 反算 Block Time */
export const STA_CHANGED_RECALC_BLOCK_TIME = "STA_CHANGED_RECALC_BLOCK_TIME";

/** Block Time 变更 → 重算 STA */
export const BLOCK_TIME_CHANGED_RECALC_STA = "BLOCK_TIME_CHANGED_RECALC_STA";

/** 机场变更 → 匹配两端机场对应的新航路 */
export const AIRPORT_CHANGED_MATCH_ROUTE = "AIRPORT_CHANGED_MATCH_ROUTE";

// ─── 规则实现 ───────────────────────────────────────────────────────────────

/**
 * @typedef {Object} RecalculationContext
 * @property {any} newValue - 变更后的新值
 * @property {any} [previousValue] - 变更前的旧值，用于跨日等需要对比前后值的派生
 * @property {Object} currentFlight - 当前航班对象（标准字段）
 * @property {Object} referenceData - 参考数据
 * @property {Record<string, Object>} [referenceData.routesById] - 以 routeId 为 key 的航路映射
 * @property {Object[]|Record<string, Object>|Map<string, Object>} [referenceData.routes] - 航路列表
 * @property {Record<string, Object[]>|Map<string, Object[]>} [referenceData.routesByLeg] - 以 dep:arr / dep-arr 为 key 的航路映射
 * @property {Object[]|Record<string, Object>|Map<string, Object>} [referenceData.aircrafts] - 飞机主数据，用于由机号解析机型
 */

/**
 * @typedef {Object} RecalculationResult
 * @property {Record<string, any>} patches - 需要回填到表单的字段更新
 * @property {string[]} warnings - 警告信息（如有）
 * @property {Object[]} validations - 约束校验结果占位，实际由 Pipeline validate 阶段填充
 */

function deriveRouteChanged(newValue, currentFlight, referenceData) {
    const route = getRouteById(referenceData, newValue);
    // Route changes must read the newly selected route object, not stale flight fallback fields.
    const { blockTimeMinutes, minBlockTimeMinutes } = getRouteTiming(route);

    if (!route && (newValue === undefined || newValue === null || newValue === "")) {
        return {
            patches: {
                sta: null,
                block_time_minutes: null,
                min_block_time_minutes: null,
                route_snapshot: null,
                flight_route_name: null,
                flight_route_block_time_minutes: null,
            },
            stopPropagationFields: ["sta", "block_time_minutes"],
            warnings: [],
        };
    }

    if (!route) {
        return {
            patches: {
                sta: null,
                block_time_minutes: null,
                min_block_time_minutes: null,
                route_snapshot: null,
                flight_route_name: null,
                flight_route_block_time_minutes: null,
            },
            stopPropagationFields: ["sta", "block_time_minutes", "min_block_time_minutes"],
            warnings: [`未找到航路 ${newValue} 的引用数据，无法按航路 BH 自动计算 STA`],
        };
    }

    const patches = {
        route_snapshot: buildRouteSnapshot(route, blockTimeMinutes, minBlockTimeMinutes),
        flight_route_name: route.route_name || null,
        flight_route_block_time_minutes: blockTimeMinutes,
        min_block_time_minutes: minBlockTimeMinutes,
    };

    if (blockTimeMinutes != null) {
        patches.block_time_minutes = blockTimeMinutes;

        const newSta = calculateSta(currentFlight?.std, blockTimeMinutes);
        if (newSta) {
            patches.sta = newSta;
        }
    } else {
        patches.block_time_minutes = null;
        patches.sta = null;
    }

    return { patches, stopPropagationFields: ["sta", "block_time_minutes", "min_block_time_minutes"], warnings: buildRouteTimingWarnings(route, currentFlight) };
}

function deriveStdChanged(newValue, currentFlight, referenceData, options = {}) {
    const currentRouteId = getCurrentRouteId(currentFlight);
    const route = getCurrentRoute(currentFlight, referenceData);
    const routeTiming = getRouteTiming(route);
    const blockTimeMinutes = currentRouteId ? routeTiming.blockTimeMinutes : getCurrentBlockTime(currentFlight);
    const newSta = calculateSta(newValue, blockTimeMinutes);
    const patches = {
        ...deriveStdDatePatches(newValue, currentFlight, options.previousValue),
        ...(currentRouteId ? {
            route_snapshot: route ? buildRouteSnapshot(route, routeTiming.blockTimeMinutes, routeTiming.minBlockTimeMinutes) : null,
            flight_route_name: route?.route_name || currentFlight?.flight_route_name || null,
            flight_route_block_time_minutes: routeTiming.blockTimeMinutes,
            block_time_minutes: routeTiming.blockTimeMinutes,
            min_block_time_minutes: routeTiming.minBlockTimeMinutes,
        } : {}),
        ...(newSta ? { sta: newSta } : {}),
    };

    if (currentRouteId && blockTimeMinutes === null) {
        patches.sta = null;
    }

    return {
        patches,
        stopPropagationFields: ["sta", "block_time_minutes", "min_block_time_minutes"],
        warnings: currentRouteId ? buildRouteTimingWarnings(route, currentFlight) : [],
    };
}

function deriveStaChanged(newValue, currentFlight, referenceData, options = {}) {
    if (!options.isDirect) {
        return { patches: {}, warnings: [] };
    }

    const blockTimeMinutes = calculateBlockTimeMinutes(currentFlight?.std, newValue);
    return {
        patches: blockTimeMinutes == null ? {} : { block_time_minutes: blockTimeMinutes },
        stopPropagationFields: ["block_time_minutes"],
        warnings: [],
    };
}

function deriveBlockTimeChanged(newValue, currentFlight) {
    const blockTimeMinutes = normalizeMinutes(newValue);
    const newSta = calculateSta(currentFlight?.std, blockTimeMinutes);

    return {
        patches: newSta ? { sta: newSta } : {},
        stopPropagationFields: ["sta"],
        warnings: [],
    };
}

function deriveAirportChanged(newValue, currentFlight, referenceData) {
    const matchingRoute = findMatchingRoute(currentFlight?.departure_airport, currentFlight?.arrival_airport, referenceData, currentFlight);
    if (matchingRoute) {
        return {
            patches: {
                flight_route_id: matchingRoute.id,
            },
            warnings: [],
        };
    }

    return {
        patches: {
            flight_route_id: null,
        },
        warnings: ["机场变更后未匹配到航路，已清除已绑定航路"],
    };
}

const derivationRules = Object.freeze([
    { id: ROUTE_CHANGED_RECALC_STA, trigger: "flight_route_id", derive: deriveRouteChanged },
    { id: STD_CHANGED_RECALC_STA, trigger: "std", derive: deriveStdChanged },
    { id: STA_CHANGED_RECALC_BLOCK_TIME, trigger: "sta", derive: deriveStaChanged },
    { id: BLOCK_TIME_CHANGED_RECALC_STA, trigger: "block_time_minutes", derive: deriveBlockTimeChanged },
    { id: AIRPORT_CHANGED_MATCH_ROUTE, trigger: "departure_airport", derive: deriveAirportChanged },
    { id: AIRPORT_CHANGED_MATCH_ROUTE, trigger: "arrival_airport", derive: deriveAirportChanged },
]);

function getRulesForField(changedField) {
    return derivationRules.filter((rule) => rule.trigger === changedField);
}

/**
 * 应用重算规则。
 *
 * @param {string} changedField - 发生变更的字段名，如 'flight_route_id'、'std'、'departure_airport' 等
 * @param {RecalculationContext} context
 * @returns {RecalculationResult}
 */
export function applyRecalculationRules(changedField, context) {
    const { newValue, currentFlight, referenceData } = context || {};
    const hasPreviousValue = Object.prototype.hasOwnProperty.call(context || {}, "previousValue");
    const initialPreviousValue = hasPreviousValue
        ? context.previousValue
        : currentFlight?.[changedField] !== newValue ? currentFlight?.[changedField] : undefined;
    const patches = {};
    const warnings = [];
    const workingFlight = {
        ...(currentFlight || {}),
        [changedField]: newValue,
    };
    const queue = [{ field: changedField, value: newValue, depth: 0, isDirect: true, previousValue: initialPreviousValue }];

    while (queue.length) {
        const current = queue.shift();
        if (current.depth > MAX_DERIVATION_DEPTH) {
            continue;
        }

        getRulesForField(current.field).forEach((rule) => {
            const result = rule.derive(current.value, workingFlight, referenceData || {}, {
                isDirect: current.isDirect,
                depth: current.depth,
                previousValue: current.previousValue,
            }) || {};
            const nextPatches = result.patches || {};
            const stopPropagationFields = new Set(result.stopPropagationFields || []);

            Object.entries(nextPatches).forEach(([field, value]) => {
                const previousValue = workingFlight[field];
                patches[field] = value;
                workingFlight[field] = value;

                if (!stopPropagationFields.has(field) && current.depth < MAX_DERIVATION_DEPTH && getRulesForField(field).length > 0) {
                    queue.push({
                        field,
                        value,
                        depth: current.depth + 1,
                        isDirect: false,
                        previousValue,
                    });
                }
            });

            warnings.push(...(result.warnings || []));
        });
    }

    return { patches, warnings, validations: [] };
}
